import React from "react";
import { isLinked, isSingleBuilding, numberedObjects, seqNames, totalSheetName } from "../../structure/structureRules";
import {
  addBuilding,
  applyNamingRule,
  describeEntity,
  moveBuilding,
  removeEntity,
  setCommon,
  setParking,
  updateEntity,
  zoneLetters,
} from "../../structure/siteOps";

const MAX_ZONES = 8;
const MAX_FLOORS = 120;

const clampInt = (value, lo, hi, fallback) => {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : Math.max(lo, Math.min(hi, n));
};

export default function PlotStep({ site, trees, onChange }) {
  const blds = site.ents.filter((e) => e.kind === "bldg");
  const single = isSingleBuilding(site);
  const common = site.ents.find((e) => e.kind === "common");
  const parking = site.ents.find((e) => e.kind === "shared");
  const series = seqNames(site.naming?.rule, Math.max(blds.length, 2));
  const objects = numberedObjects(site);

  const setSite = (next) => onChange(next, trees);
  const edit = (id, patch) => setSite(updateEntity(site, id, patch));

  return (
    <div className="bgrid">
      <div className="bcol">
        <section className="bcard">
          <div className="bcard-h">
            <h4>ნაკვეთი</h4>
            <p>დასახელება; ფართობი არასავალდებულოა</p>
          </div>
          <div className="fgrid g2">
            <div className="fld">
              <label htmlFor="ps-plot">დასახელება</label>
              <input id="ps-plot" value={site.name || ""} onChange={(e) => setSite({ ...site, name: e.target.value })} />
            </div>
            <div className="fld">
              <label htmlFor="ps-plot-area">ფართობი, მ²</label>
              <input
                id="ps-plot-area"
                className="num"
                inputMode="decimal"
                value={site.area || ""}
                onChange={(e) => setSite({ ...site, area: e.target.value })}
              />
            </div>
          </div>
        </section>

        <section className="bcard">
          <div className="bcard-h">
            <h4>შენობები</h4>
            <p>სახელები წესით; ზონები და სართულები თითოეულს</p>
          </div>
          <div className="fld">
            <label htmlFor="ps-rule">სახელების წესი</label>
            <input
              id="ps-rule"
              value={site.naming?.rule || ""}
              placeholder="კორპუსი 1, კორპუსი 2"
              onChange={(e) => setSite(applyNamingRule(site, e.target.value))}
            />
            <div className={`snote${series.ok ? "" : " bad"}`}>
              {series.ok
                ? `შემდეგი: ${seqNames(site.naming?.rule, blds.length + 2).names.slice(blds.length, blds.length + 2).join(", ")} …`
                : "სერია ვერ ამოვიცანი — დამატებითი შენობები „შენობა N“ დაერქმევა"}
            </div>
          </div>
          <div className="btab">
            <div className="brow h">
              <span />
              <span>დასახელება</span>
              <span>ზონები</span>
              <span>მიწისქვეშა სართ.</span>
              <span>მიწისზედა სართ.</span>
              <span />
            </div>
            {blds.map((b, i) => (
              <div className="brow" key={b.id}>
                <span className="no">{objects.find((o) => o.id === b.id)?.number}</span>
                <input className="inp" value={b.name} onChange={(e) => edit(b.id, { name: e.target.value })} aria-label="დასახელება" />
                <input
                  className="inp num"
                  value={b.zones ?? 1}
                  inputMode="numeric"
                  onChange={(e) => edit(b.id, { zones: clampInt(e.target.value, 1, MAX_ZONES, 1) })}
                  aria-label="ზონები"
                />
                <input
                  className="inp num"
                  value={b.down ?? 0}
                  inputMode="numeric"
                  onChange={(e) => edit(b.id, { down: clampInt(e.target.value, 0, MAX_FLOORS, 0) })}
                  aria-label="მიწისქვეშა სართულები"
                />
                <input
                  className="inp num"
                  value={b.up ?? 1}
                  inputMode="numeric"
                  onChange={(e) => edit(b.id, { up: clampInt(e.target.value, 1, MAX_FLOORS, 1) })}
                  aria-label="მიწისზედა სართულები"
                />
                <span className="acts">
                  <button type="button" className="ib xs" disabled={i === 0} onClick={() => setSite(moveBuilding(site, b.id, -1))} title="ზემოთ">
                    ▲
                  </button>
                  <button
                    type="button"
                    className="ib xs"
                    disabled={blds.length === 1}
                    onClick={() => {
                      const r = removeEntity(site, trees, b.id);
                      onChange(r.site, r.trees);
                    }}
                    title="წაშლა"
                  >
                    ✕
                  </button>
                </span>
              </div>
            ))}
          </div>
          <div>
            <button type="button" className="btn xs" onClick={() => setSite(addBuilding(site))}>
              + შენობა
            </button>
          </div>
          <div className="snote">ტიპიური სართულები: 2 … ზედა−1 (ერთხელ შეიყვანება და მრავლდება).</div>
        </section>

        <section className="bcard">
          <div className="bcard-h">
            <h4>საერთო ნაწილები</h4>
            <p>ეზო და ქსელები; მიწისქვეშა პარკინგი შენობების ქვეშ</p>
          </div>
          {single ? (
            <div className="snote">
              ერთი შენობისას საერთო ნაწილი არ არის: ეზო და ქსელები შენობის დისციპლინაა („გარე სამუშაოები“), მიწისქვეშა სართულები —
              შენობის ნაწილი.
            </div>
          ) : (
            <>
              <label className="lreq">
                <input type="checkbox" checked={!!common} onChange={(e) => setSite(setCommon(site, e.target.checked))} />
                საერთო სამუშაოები — ეზო, გარე ქსელები, მოსამზადებელი
              </label>
              <div className="prow">
                <label className="lreq" style={{ fontWeight: 400 }}>
                  <input type="checkbox" checked={!!parking} onChange={(e) => setSite(setParking(site, e.target.checked))} />
                  მიწისქვეშა პარკინგი შენობების ქვეშ
                </label>
                {parking && (
                  <>
                    <input
                      className="inp num"
                      style={{ width: 60 }}
                      value={parking.down ?? 1}
                      onChange={(e) => edit(parking.id, { down: clampInt(e.target.value, 1, 10, 1) })}
                      aria-label="პარკინგის სართულები"
                    />
                    <span className="snote">სართული</span>
                  </>
                )}
              </div>
              {parking && (
                <div className="prow">
                  {blds.map((b) => (
                    <label key={b.id} className="lreq" style={{ fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={(parking.serves || []).includes(b.id)}
                        onChange={(e) =>
                          edit(parking.id, {
                            serves: e.target.checked ? [...(parking.serves || []), b.id] : (parking.serves || []).filter((x) => x !== b.id),
                          })
                        }
                      />
                      {b.name}
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <div className="bcol">
        <section className="bcard">
          <div className="bcard-h">
            <h4>ნაკვეთის გეგმა</h4>
            <p>{site.name || "ნაკვეთი"}{site.area ? ` · ${site.area} მ²` : ""}</p>
          </div>
          <div className="site" aria-label="ნაკვეთის გეგმა">
            {blds.map((b) => (
              <div key={b.id} className={`bld${isLinked(site, b) ? " linked" : ""}`}>
                <span className="t">
                  {objects.find((o) => o.id === b.id)?.number} · {b.name}
                </span>
                <span className="zones" title="ზონა — შენობის ნაწილი, გამოყოფილი სეისმური ნაკერით ან ეტაპით. რაოდენობა: სვეტი „ზონები“.">
                  {zoneLetters(b.zones).map((z) => (
                    <span key={z}>ზონა {z}</span>
                  ))}
                </span>
                <span className="f">
                  −{b.down ?? 0} / {b.up ?? 1} სართ.
                </span>
              </div>
            ))}
            {!single && parking && <div className="park">P · {describeEntity(site, parking)}</div>}
            {!single && common && <div className="common">0 საერთო: ეზო, ქსელები</div>}
          </div>
          <div>
            <div className="pcap">ობიექტები — თითოს თავისი TOTAL</div>
            <div className="olist" data-no-translate="true">
              {objects.map((o) => (
                <div className="orow" key={o.id}>
                  <span>{o.number}</span>
                  <b>{o.name}</b>
                  <span className="od">{describeEntity(site, o)}</span>
                  <span className="ot">{totalSheetName(site, o.number)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
